import { createFileRoute, useLocation, useNavigate } from '@tanstack/react-router'
import pluralize from 'pluralize'
import { type JSX, useMemo, useState } from 'react'
import { AvatarStack } from '~/features/avatar'
import { RoleMutators } from '~/mutators/roles'
import { useTRPC } from '~/services/trpc'
import { SettingsActions, SettingsContent, useSettingTitle } from '~/templates/settings'

import { Button, Icon, Search } from '@hap/components'

import { useQuery } from "@tanstack/react-query";

export const Route = createFileRoute('/_authenticated/$alias/settings/roles/')({
  component: RouteComponent,
})

function RouteComponent(): JSX.Element {
  const trpc = useTRPC();
  const location = useLocation()
  const navigate = useNavigate({ from: '/$alias/settings' })
  const { data: roles } = useQuery(trpc.roles.list.queryOptions())

  const { create, isPending } = RoleMutators.useCreate({
    onSuccess: async (roleId) => {
      navigate({
        to: `/$alias/settings/roles/${roleId}`,
        search: location.search,
      })
    },
  })

  const [searchQuery, setSearchQuery] = useState('')

  const filteredRoles = useMemo(() => {
    if (!roles || !searchQuery) return roles
    const q = searchQuery.toLowerCase()
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(q) ||
        role.description?.toLowerCase().includes(q),
    )
  }, [roles, searchQuery])

  useSettingTitle(['Roles'])

  return (
    <>
      <SettingsActions>
        <Button onClick={create} loading={isPending} loadingLabel="Creating...">
          Create Role
        </Button>
      </SettingsActions>
      <SettingsContent spacing="sm" className="w-full overflow-y-auto">
        <header className="mb-10 flex items-center justify-between gap-4">
          <Search placeholder="Search roles" onSearch={setSearchQuery} />
        </header>
        <div className="grid gap-3">
          {filteredRoles?.map((role) => (
            <button
              onClick={() =>
                navigate({
                  to: `/$alias/settings/roles/${role.id}`,
                  search: location.search,
                })
              }
              key={role.id}
              className="hover:bg-accent flex items-center justify-between gap-4 rounded-md border p-4 text-left"
            >
              <div className="shrink grow">
                <p className="flex items-center gap-2 text-lg font-semibold">
                  {role.name} {role.isSystemRole && <Icon name="lock-simple" />}
                </p>
                <p className="text-accent-foreground">{role.description}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <AvatarStack members={role.members} total={role.memberCount} />
                <span className="text-accent-foreground text-sm">
                  {role.memberCount} {pluralize('member', role.memberCount)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </SettingsContent>
    </>
  )
}
